import * as React from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { cn } from "@/lib/utils";

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  strength?: number;
  children: React.ReactNode;
};

export function MagneticButton({ className, children, strength = 30, onMouseMove, onMouseLeave, ...rest }: Props) {
  const ref = React.useRef<HTMLButtonElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const springX = useSpring(x, { stiffness: 180, damping: 14, mass: 0.5 });
  const springY = useSpring(y, { stiffness: 180, damping: 14, mass: 0.5 });

  const innerX = useTransform(springX, v => v * 0.5);
  const innerY = useTransform(springY, v => v * 0.5);

  const handleMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = ((e.clientX - cx) / r.width) * strength;
    const dy = ((e.clientY - cy) / r.height) * strength;
    x.set(dx);
    y.set(dy);
    onMouseMove?.(e);
  };

  const handleLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    x.set(0);
    y.set(0);
    onMouseLeave?.(e);
  };

  return (
    <motion.button
      ref={ref}
      style={{ x: springX, y: springY }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={cn(
        "relative inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-medium",
        "bg-accent text-bg shadow-glow hover:shadow-[0_0_36px_-8px_#e0c388] transition-shadow",
        className
      )}
      {...(rest as any)}
    >
      <motion.span style={{ x: innerX, y: innerY }} className="pointer-events-none flex items-center gap-2">
        {children}
      </motion.span>
    </motion.button>
  );
}
